import { TTypographyLevels } from '@lib/theme/typography.types';
import React from 'react';
import { DefaultTheme, useTheme } from 'styled-components';
import { TButtonShape, TButtonSize, TButtonSurface, TButtonVariant } from './icon-button.types';

export const buttonSize: Record<TButtonSize, number> = {
  'extra-small': 24,
  small: 32,
  medium: 40,
  large: 48,
  'extra-large': 64,
};

export const iconSize: Record<TButtonSize, number> = {
  'extra-small': 20,
  small: 20,
  medium: 20,
  large: 24,
  'extra-large': 30,
};

export const loaderStyle: Record<TButtonVariant, 'on-dark' | 'on-light'> = {
  primary: 'on-light',
  secondary: 'on-light',
  'ghost-tertiary': 'on-light',
  'danger-outlined': 'on-light',
  'ghost-secondary': 'on-light',
  outline: 'on-light',
  'ghost-primary': 'on-light',
  danger: 'on-light',
};

export const loaderSize: Record<TButtonSize, 'medium' | 'small'> = {
  'extra-small': 'small',
  large: 'medium',
  small: 'small',
  medium: 'small',
  'extra-large': 'medium',
};

export const buttonTextConfig: Record<
  TButtonSize,
  { display: TTypographyLevels; variant: 'default' }
> = {
  large: {
    display: 'captionLarge',
    variant: 'default',
  },
  medium: {
    display: 'captionSmall',
    variant: 'default',
  },
  small: {
    display: 'captionSmall',
    variant: 'default',
  },
  'extra-large': {
    display: 'captionLarge',
    variant: 'default',
  },
  'extra-small': {
    display: 'captionSmall',
    variant: 'default',
  },
};

export const overlayConfig: Record<TButtonSurface, Record<TButtonVariant, 'light' | 'dark'>> = {
  'on-dark': {
    primary: 'dark',
    secondary: 'light',
    'danger-outlined': 'dark',
    'ghost-tertiary': 'dark',
    'ghost-secondary': 'dark',
    'ghost-primary': 'dark',
    outline: 'dark',
    danger: 'light',
  },
  'on-light': {
    primary: 'dark',
    secondary: 'light',
    'danger-outlined': 'light',
    'ghost-tertiary': 'light',
    'ghost-secondary': 'light',
    'ghost-primary': 'light',
    outline: 'light',
    danger: 'light',
  },
};

type TUseButtonStyle = {
  variant: TButtonVariant;
  size: TButtonSize;
  shape: TButtonShape;
  surface: TButtonSurface;
};

export function getButtonColors({
  variant,
  theme,
  surface,
}: {
  variant: TButtonVariant;
  theme: DefaultTheme;
  surface: TButtonSurface;
}) {
  let color = 'transparent';
  let iconColor = 'transparent';
  let borderColor = null;
  let backgroundColor = 'transparent';

  switch (variant) {
    case 'primary':
      backgroundColor = theme.color.surface.primary;
      iconColor = theme.color.tint.onPrimary;
      color = theme.color.tint.action;
      break;
    case 'secondary':
      backgroundColor = theme.color.surface.secondary;
      iconColor = theme.color.tint.action;
      color = theme.color.tint.secondary;
      break;
    case 'danger':
      backgroundColor = theme.color.solid.error;
      iconColor = theme.color.tint.onSolid;
      color = theme.color.tint.error;
      break;
    case 'outline':
      iconColor = theme.color.tint.action;
      color = theme.color.tint.secondary;
      borderColor = theme.color.border.regular;
      break;
    case 'ghost-tertiary':
      iconColor = theme.color.tint.tertiary;
      color = theme.color.tint.tertiary;
      break;
    case 'ghost-secondary':
      iconColor = theme.color.tint.action;
      color = theme.color.tint.action;
      break;
    case 'ghost-primary':
      iconColor = theme.color.tint.highlight;
      color = theme.color.tint.highlight;
      break;
    case 'danger-outlined':
      iconColor = theme.color.tint.error;
      color = theme.color.tint.error;
      break;
  }

  if (surface === 'on-dark') {
    switch (variant) {
      case 'primary':
        color = theme.color.tint.actionDark;
        break;
      case 'secondary':
        backgroundColor = theme.color.surface.secondaryDark;
        iconColor = theme.color.tint.actionDark;
        color = theme.color.tint.secondaryDark;
        break;
      case 'danger':
        color = theme.color.tint.errorDark;
        break;
      case 'outline':
        iconColor = theme.color.tint.actionDark;
        color = theme.color.tint.secondaryDark;
        borderColor = theme.color.border.regularDark;
        break;
      case 'ghost-tertiary':
        iconColor = theme.color.tint.tertiaryDark;
        color = theme.color.tint.tertiaryDark;
        break;
      case 'ghost-secondary':
        iconColor = theme.color.tint.actionDark;
        color = theme.color.tint.actionDark;
        break;
      case 'danger-outlined':
        iconColor = theme.color.tint.errorDark;
        color = theme.color.tint.errorDark;
        break;
    }
  }

  return {
    color,
    iconColor,
    borderColor,
    backgroundColor,
  };
}

export function useButtonStyle(params: TUseButtonStyle) {
  const theme = useTheme();

  return React.useMemo(() => {
    return {
      ...getButtonColors({ variant: params.variant, theme, surface: params.surface }),
      textConfig: buttonTextConfig[params.size],
      loaderStyle: loaderStyle[params.variant],
      iconSize: iconSize[params.size],
      buttonSize: buttonSize[params.size],
      borderRadius: params.shape === 'round' ? '50%' : `${theme.radius.medium}px`,
    };
  }, [theme, params.variant, params.size, params.shape, params.surface]);
}
